/**
 * CountdownClocks — horizontal row of per-clock countdown cards for a judgment.
 *
 * Renders only the clocks relevant to the current state's variant:
 *   - Always: Judgment Expiration
 *   - Non-hard-cutoff: Renewal Deadline
 *   - Any: Lien Re-Docket (if lien_differs_from_judgment or lien_filed)
 *   - Dormancy states: Dormancy Trigger
 *   - Idaho: extra "11y Action-on-Judgment" clock
 *   - Florida: extra "Tranche 2 (re-record)" clock
 *   - Illinois: extra "Revival Outer Limit" clock
 */
import React from "react";
import { CalendarClock, Flag, LandPlot, Clock, FileText, HelpCircle } from "lucide-react";
import { useJudgmentTimeline } from "@/hooks/useJudgmentLaw";
import JeaCard from "@/components/jea/JeaCard";
import { cn } from "@/lib/utils";

const bandColor = (days, isExpired) => {
  if (isExpired || (days != null && days < 0)) return { bg: "bg-bg-tertiary", text: "text-text-muted", ring: "border-border-default" };
  if (days == null) return { bg: "bg-bg-tertiary", text: "text-text-secondary", ring: "border-border-default" };
  if (days < 90) return { bg: "bg-danger/15", text: "text-danger animate-pulse", ring: "border-danger/50" };
  if (days < 365) return { bg: "bg-danger/10", text: "text-danger", ring: "border-danger/40" };
  if (days < 730) return { bg: "bg-warning/10", text: "text-warning", ring: "border-warning/40" };
  return { bg: "bg-success/10", text: "text-success", ring: "border-success/40" };
};

const shortDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const addYears = (iso, years) => {
  if (!iso) return null;
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  const now = Date.now();
  return { iso: d.toISOString().slice(0, 10), days_until: Math.round((d.getTime() - now) / 86400000) };
};

const ClockCard = ({ Icon, label, days, iso, expired, tip, testid }) => {
  const c = bandColor(days, expired);
  const display = expired || (days != null && days < 0)
    ? "EXPIRED"
    : days != null
    ? `${days}`
    : "—";
  return (
    <div
      className={cn("relative rounded-[12px] border p-4 min-w-[168px] flex-1 min-w-0", c.bg, c.ring)}
      data-testid={testid}
    >      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-text-secondary">
          <Icon size={12} className="text-accent-pink" />
          {label}
        </div>
        <span title={tip} className="text-text-secondary hover:text-accent-pink cursor-help">
          <HelpCircle size={12} />
        </span>
      </div>
      <div className={cn("jea-mono text-[28px] font-extrabold leading-none", c.text)}>
        {display}
      </div>
      <div className="mt-1 text-[11px] text-text-secondary jea-mono">
        {expired || (days != null && days < 0) ? "past deadline" : "days"} · {shortDate(iso)}
      </div>
    </div>
  );
};

export const CountdownClocks = ({ payload, testid = "countdown-clocks" }) => {
  const { data, loading, error } = useJudgmentTimeline(payload);

  if (loading || error || !data?.known) return null;

  const dates = data.dates || {};
  const clocks = [];

  clocks.push({
    key: "judgment_expiration",
    Icon: CalendarClock,
    label: "Judgment Expiration",
    days: dates.judgment_expires?.days_until,
    iso: dates.judgment_expires?.iso,
    expired: !!data.expired,
    tip: data.hard_cutoff
      ? "Hard cutoff — no renewal available. Enforcement must complete before this date."
      : `Renewal via ${data.renewal_mechanism || "new action"}.`,
  });

  if (!data.hard_cutoff && dates.renewal_deadline?.iso) {
    clocks.push({
      key: "renewal_deadline",
      Icon: Flag,
      label: "Renewal Deadline",
      days: dates.renewal_deadline?.days_until,
      iso: dates.renewal_deadline?.iso,
      expired: false,
      tip: `File renewal via ${data.renewal_mechanism || "new action"} before this date.`,
    });
  }

  if ((data.lien_differs_from_judgment || data.lien_filed) && dates.lien_expires?.iso) {
    clocks.push({
      key: "lien_redocket",
      Icon: LandPlot,
      label: data.variant_key === "NY" || data.variant_key === "WI" ? "Lien Re-Docket" : "Lien Expires",
      days: dates.lien_expires?.days_until,
      iso: dates.lien_expires?.iso,
      expired: false,
      tip: data.lien_life_note || "Re-record or re-docket per state rules.",
    });
  }

  if (dates.dormancy_trigger?.iso) {
    clocks.push({
      key: "dormancy_trigger",
      Icon: Clock,
      label: "Dormancy Trigger",
      days: dates.dormancy_trigger?.days_until,
      iso: dates.dormancy_trigger?.iso,
      expired: false,
      tip: "Judgment becomes dormant on this date — revival required.",
    });
  }

  // Idaho — action-on-judgment 11y
  if (data.variant_key === "ID") {
    const entered = dates.judgment_entered?.iso;
    const aoj = addYears(entered, 11);
    if (aoj) {
      clocks.push({
        key: "id_action_on_judgment",
        Icon: FileText,
        label: "Idaho Action-on-Judgment",
        days: aoj.days_until,
        iso: aoj.iso,
        expired: false,
        tip: "Idaho — 11-year action-on-judgment clock alongside 10-year enforcement.",
      });
    }
  }

  // Florida — Tranche 2 (re-record) 20y from entry
  if (data.variant_key === "FL") {
    const entered = dates.judgment_entered?.iso;
    const tranche2 = addYears(entered, 20);
    if (tranche2) {
      clocks.push({
        key: "fl_tranche_2",
        Icon: LandPlot,
        label: "Florida Tranche 2",
        days: tranche2.days_until,
        iso: tranche2.iso,
        expired: false,
        tip: "Re-record judgment lien to preserve the second 10-year tranche.",
      });
    }
  }

  // Illinois — 20y revival outer limit
  if (data.variant_key === "IL") {
    const entered = dates.judgment_entered?.iso;
    const outer = addYears(entered, 20);
    if (outer) {
      clocks.push({
        key: "il_revival_outer",
        Icon: FileText,
        label: "Illinois Revival Outer",
        days: outer.days_until,
        iso: outer.iso,
        expired: false,
        tip: "Illinois — revival petition must be filed within 20 years of entry.",
      });
    }
  }

  return (
    <JeaCard data-testid={testid}>
      <div className="jea-caption mb-3">Countdown Clocks</div>
      <div className="flex gap-3 flex-wrap">
        {clocks.map(({ key, ...rest }) => (
          <ClockCard key={key} {...rest} testid={`countdown-clock-${key}`} />
        ))}
      </div>
    </JeaCard>
  );
};

export default CountdownClocks;
